import { GraphQLResult } from 'aws-amplify/api';
import { useState } from 'react';
import { Button, Flex, Input, Text, VStack } from '@chakra-ui/react';
import { isBefore } from 'date-fns';
import { migrateMeetingStatsV102 } from '@/backend/migrations/meetingStatsMigrations';
import { mutationDeleteCaseStats } from '@/features/meeting-stats/graphql/delete';
import { queryListNpsScoresForMeeting } from '@/features/meeting-stats/graphql/queries';
import { startMock } from '@/features/meeting-stats/mocking-tool';
import clearAllMockingData from '@/features/meeting-stats/mocking-tool/general/clearAllMockingData';
import {
  ListMeetingHostEventsQuery,
  ListMeetingHostEventsQueryVariables,
  ListMeetingsQuery,
  ListMeetingsQueryVariables,
  ListMeetingStatsQuery,
  ListMeetingStatsQueryVariables,
  MeetingStats,
  UpdateMeetingStatsMutationVariables,
} from '@/services/API';
import { updateMeetingStats } from '@/services/graphql/mutations';
import { listMeetingHostEvents, listMeetings, listMeetingStats } from '@/services/graphql/queries';
import PageLayout from '@/ui/Layout/PageLayout';
import { callGraphQLApi } from '@/utils/graphQLAPI';

type Args<T, R> = {
  entireInput: T[];
  fn: (input: T[]) => Promise<R>;
  batchSize?: number;
  batchName: string;
};

export async function runBatchedFunction<T, R>({
  entireInput,
  fn,
  batchSize: batchSizeOverride,
  batchName,
}: Args<T, R>): Promise<R[]> {
  const batchSize = batchSizeOverride || 25;
  const inputSize = entireInput.length;
  const allResults: R[] = [];
  console.log(
    `[Batch] ${batchName} Starts with with input length: ${inputSize} and batch size ${batchSize}`
  );
  for (let i = 0; i < inputSize; i += batchSize) {
    const batch = entireInput.slice(i, i + batchSize);
    console.log(
      `[Batch] ${batchName} running next batch with length: ${
        batch.length
      } now. (${i < inputSize ? i : inputSize}/${inputSize})`
    );
    const batchResut = await fn(batch);
    allResults.push(batchResut);
  }
  return allResults;
}

async function handleMeetingStatsMigration() {
  const variables: ListMeetingStatsQueryVariables = {};
  const meetingStatsResponse = await callGraphQLApi<GraphQLResult<ListMeetingStatsQuery>>(
    listMeetingStats,
    variables
  );
  const meetingStats = meetingStatsResponse.data?.listMeetingStats?.items as MeetingStats[];

  if (!meetingStats) {
    console.error('No meeting stats found');
    return;
  }
  console.log('DEBUG >> amt meeting stats', meetingStats.length);
  await Promise.all(
    meetingStats.map(async (meetingStat) => {
      console.log('DEBUG >> ', meetingStat.version);
      const migratedMeetingStat = await migrateMeetingStatsV102(meetingStat);
      if (migratedMeetingStat) {
        const variables: UpdateMeetingStatsMutationVariables = {
          input: {
            id: meetingStat.id,
            version: migratedMeetingStat.version,
            casePresentationHistory: migratedMeetingStat.casePresentationHistory,
          },
        };
        console.log(
          'DEBUG >> needs update',
          meetingStat.casePresentationHistory,
          migratedMeetingStat.casePresentationHistory
        );
        await callGraphQLApi(updateMeetingStats, variables);
      }
    })
  );

  console.log('done migrating meeting stats');
}

async function handleRemoveCaseStatsNotPartOfCaseCluster() {
  const variables: ListMeetingsQueryVariables = {};
  const meetingsResponse = await callGraphQLApi<GraphQLResult<ListMeetingsQuery>>(
    listMeetings,
    variables
  );
  const meetings = meetingsResponse.data?.listMeetings?.items || [];
  console.log('DEBUG >> amt meetings', meetings.length);
  await Promise.all(
    meetings.map(async (meeting) => {
      if (!meeting) return;
      const meetingStats = meeting.caseStats?.items || [];
      const caseIds = meeting.caseCluster?.cases || [];

      if (caseIds.length === 0 || meetingStats.length === 0) {
        console.warn(
          'DEBUG >> caseIds or meetingStats is empty, skipping this meeting, ',
          meeting.id,
          caseIds,
          meetingStats
        );
        return;
      }
      await Promise.all(
        meetingStats.map(async (meetingStat) => {
          if (!meetingStat) {
            console.warn('DEBUG >> meetingStat is empty, skipping', meeting.id);
            return;
          }
          const found = caseIds.find((caseId) => caseId === meetingStat?.caseId);
          if (!found) {
            console.log('DEBUG >> needs change', meeting.id, meetingStat?.caseId);
            await mutationDeleteCaseStats(meetingStat.id);
          }
        })
      );
    })
  );
  console.log('DEBUG >> done removing caseStats not part of caseCluster');
}

async function handleFixMeetingUserNPSCoreStats() {
  const variables: ListMeetingsQueryVariables = {};
  const meetingsResponse = await callGraphQLApi<GraphQLResult<ListMeetingsQuery>>(
    listMeetings,
    variables
  );
  const meetings = meetingsResponse.data?.listMeetings?.items || [];
  console.log('DEBUG >> amt meetings', meetings.length);

  const allBackupData: Record<string, any> = {};

  await runBatchedFunction({
    entireInput: meetings,
    batchName: 'meetingRemoveNPSScoreDoubles',
    batchSize: 1,
    fn: async (batchOfMeetings) => {
      await Promise.all(
        batchOfMeetings.map(async (meeting) => {
          if (!meeting) return;
          const userNPSScores = await queryListNpsScoresForMeeting(meeting.id);

          const allVotes = userNPSScores.data?.npsScoresByMeeting?.items ?? [];

          const latestVotePerUser =
            allVotes.reduce(
              (acc, curr) => {
                if (!curr) return acc;
                const existingVote = acc[curr.userID];
                if (!existingVote) {
                  acc[curr.userID] = {
                    timestamp: curr.createdAt,
                    voteId: curr.id,
                  };
                } else if (isBefore(new Date(existingVote.timestamp), new Date(curr.createdAt))) {
                  acc[curr.userID] = {
                    timestamp: curr.createdAt,
                    voteId: curr.id,
                  };
                }
                return acc;
              },
              {} as Record<
                string,
                {
                  voteId: string;
                  timestamp: string;
                }
              >
            ) ?? {};

          const voteIDsToKeep = Object.values(latestVotePerUser).map((el) => el.voteId);
          const votesToDelete =
            allVotes.filter((el) => el && voteIDsToKeep.includes(el.id) === false) ?? [];

          if (voteIDsToKeep.length !== allVotes.length) {
            console.log(
              'DEBUG >> totalVotes',
              allVotes.length,
              ' votesToKeep',
              voteIDsToKeep.length,
              ' votesToDelete',
              votesToDelete.length,
              latestVotePerUser,
              allVotes
            );

            allBackupData[meeting.id] = allVotes;
          }

          await runBatchedFunction({
            entireInput: votesToDelete,
            batchSize: 25,
            batchName: 'deleteNPSScore',
            fn: async (batchOfVotesToDelete) => {
              await Promise.all(
                batchOfVotesToDelete.map(async (vote) => {
                  if (!vote) return;
                  // const variables: DeleteMeetingNPSScoreMutationVariables = {
                  //   input: {
                  //     id: vote.id,
                  //   },
                  // };
                  // await callGraphQLApi(deleteMeetingNPSScore, variables);
                })
              );
            },
          });
        })
      );
    },
  });

  console.log('DEBUG >> done removing double nps scores for users');
  console.log('DEBUG >> allbackupData', allBackupData);
}

async function printHostEvents(meetingId: string) {
  const variables: ListMeetingHostEventsQueryVariables = {
    filter: {
      meetingID: {
        eq: meetingId,
      },
    },
  };
  const response = await callGraphQLApi<GraphQLResult<ListMeetingHostEventsQuery>>(
    listMeetingHostEvents,
    variables
  );
  const hostEvents = response.data?.listMeetingHostEvents?.items || [];

  if (hostEvents.length === 0) {
    console.log('No host events found for meeting:', meetingId);
    return;
  }
  console.log(`Host events for meeting ${meetingId}:`);
  console.log(hostEvents);
}

function AdminTestPage() {
  const [meetingId, setMeetingId] = useState<string>('');
  return (
    <PageLayout>
      <Flex alignItems="center" justifyContent="center" gap="20px">
        <VStack maxWidth="300px">
          <Text fontSize="30px" color="livelinx.purple200">
            Meeting mocking
          </Text>
          <Button width="full" variant="primary" onClick={() => startMock(true)}>
            Create a new mock meeting
          </Button>

          <Button width="full" variant="primary" onClick={clearAllMockingData}>
            Delete mocking data
          </Button>

          <Button width="full" variant="primary" onClick={handleMeetingStatsMigration}>
            Migrate meeting stats
          </Button>
          <Button
            width="full"
            variant="primary"
            onClick={() => {
              handleRemoveCaseStatsNotPartOfCaseCluster();
            }}
          >
            Remove caseStats not part of the casecluster
          </Button>
          <Button
            width="full"
            variant="primary"
            onClick={() => {
              handleFixMeetingUserNPSCoreStats();
            }}
          >
            Delete double user NPS Vote records
          </Button>
          <Input value={meetingId} onChange={(e) => setMeetingId(e.target.value)} />
          <Button onClick={() => printHostEvents(meetingId)}>Print host events</Button>
        </VStack>
      </Flex>
    </PageLayout>
  );
}

export default AdminTestPage;
